import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { QaiCallVideoComponent, QaiCallAudioIndicatorComponent } from '@qai/ui';
import { ControlService } from '../../../library/control.service';
import { RealtimeService } from '../../../library/realtime/realtime.service';
import { CallStoreService } from '../../../library/call-store/call-store.service';
import { DeviceSettingsService } from '../../../library/device-settings.service';
import { PlayerService } from '../../../library/player.service';
import { TranslocoService } from '@jsverse/transloco';
import { VisualGuideComponent } from '../../../call-practice-visual-aid/visual-aid-call-navigation/visual-guide.component';
import { CallHintComponent } from '../../../call-practice/call-hint/call-hint';
import { injectAiNotice } from 'src/app/pages/learner/call/library/ai-notice/ai-notice';
import { AvatarPlayerDirective } from 'src/app/directives/avatar-player.directive';

@Component({
  imports: [CommonModule, QaiCallVideoComponent, QaiCallAudioIndicatorComponent, VisualGuideComponent, CallHintComponent, AvatarPlayerDirective],
  selector: 'app-call-pathway-guide',
  standalone: true,
  styleUrls: ['./call-pathway-guide.component.css'],
  templateUrl: './call-pathway-guide.component.html',
})
export class CallPathwayGuideComponent implements OnInit, AfterViewChecked, OnDestroy {
  // The account's AI notice in the sim's or learner's language; null hides it.
  protected readonly aiNotice = injectAiNotice();

  @ViewChild('chromaCanvas') chromaCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('avatarVideoRealtime') avatarVideoRef!: ElementRef<HTMLVideoElement>;

  userName = { first: '', last: '', full: '' };

  @Input() controlService: ControlService | undefined;
  @Input() realtimeService: RealtimeService | undefined;
  @Input() callStore: CallStoreService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() hideUserVideo: boolean | undefined = false;
  @Input() isVisualAidCall: boolean = false;

  private chromaKeyStarted = false;
  private callStoreService = inject(CallStoreService);
  visualAidAnimation = false;
  visualAidOpen = false;

  constructor(private translocoService: TranslocoService) {
    effect(() => {
      const isOpen = this.callStoreService.getIsVisualAidOpen();
      if (isOpen) {
        this.visualAidAnimation = true;
        this.visualAidOpen = true;
        setTimeout(() => {
          this.visualAidAnimation = false;
        }, 800);
      }
    });
  }

  get avatarName(): string {
    return this.playerService?.avatarName ?? '';
  }

  ngOnInit() {
    this.userName = this.getUserName();
  }

  private getUserName() {
    const fallbackName =
      this.translocoService.translate('USER_FALLBACK', {}, 'learningPathway') || 'User';
    const firstName =
      localStorage.getItem('user_first_name')?.trim() || fallbackName;
    const lastName = localStorage.getItem('user_last_name')?.trim() || '';

    return {
      first: firstName,
      last: lastName,
      full: `${firstName} ${lastName}`.trim(),
    };
  }

  protected get showUserInitials() {
    return (
      this.callStore?.getIsPaused() ||
      this.deviceSettingsService?.isCameraUnavailable ||
      !this.controlService?.isVideoOn
    );
  }

  protected get showAvatarSpeakingBorder() {
    return this.callStore?.getIsAvatarSpeaking() ?? false;
  }

  protected get showAvatarSpeakingIndicator() {
    return this.callStore?.getIsAvatarSpeaking() ?? false;
  }

  protected get showAvatarLoadingIndicator() {
    return (
      this.callStore?.getIsAvatarThinking() &&
      !this.callStore?.getIsAvatarSpeaking()
    );
  }

  protected get showUserSpeakingBorder() {
    return !this.callStore?.getIsAvatarSpeaking();
  }

  protected get showUserSpeakingIndicator() {
    if (this.realtimeService?.isFirstMessage) {
      return false;
    }

    return (
      (this.callStore?.getIsMicOn() ?? false) &&
      (this.deviceSettingsService?.isUserSpeaking ?? false)
    );
  }

  protected get shouldShowUserVideo(): boolean {
    // If hideUserVideo is true (preamble step), don't show user video at all
    return this.hideUserVideo !== true;
  }

  ngAfterViewChecked(): void {
    if (
      this.avatarVideoRef &&
      this.chromaCanvasRef &&
      !this.chromaKeyStarted &&
      this.realtimeService?.customBackgroundUrl
    ) {
      const video = this.avatarVideoRef.nativeElement;
      const canvas = this.chromaCanvasRef.nativeElement;
      if (video && canvas) {
        this.chromaKeyStarted = true;
        console.log('Starting chroma keying with custom background URL:', this.realtimeService.customBackgroundUrl);
        this.realtimeService?.startChromaKeying(video, canvas, this.realtimeService.customBackgroundUrl);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.realtimeService) {
      this.realtimeService.stopChromaKeying();
    }
  }
}
